import "../assets/_global.scss";

import { Header, HeaderData } from "../components/Header/Header";

import { Footer, FooterData } from "../components/Footer/Footer";
import { Layout } from "../components/Layout/Layout";
import React, { useEffect, useState } from "react";
import { SEO, SEOData } from "../components/Seo/Seo";
import { graphql } from "gatsby";
import { COOKIE_EXPIRATION, getExpirationYears } from "../utils";
import * as styles from "./page.module.scss";
import { BlockContent } from "../components/BlockContent/BlockContent";

const { stripSlashes } = require("../lib/helpers");
const {
  getBasePathForLanguage,
  defaultLanguageCode,
} = require("../../sanity/plugins/multilanguage");

export const query = graphql`
  query PageQuery($id: String, $language: String) {
    sanityStartPage(id: { eq: $id }) {
      id
      language
      title
      _rawPageContent(resolveReferences: { maxDepth: 10 })
    }
    sanityPage(id: { eq: $id }) {
      id
      language
      title
      pageRedirect {
        redirectTarget {
          ... on SanityStartPage {
            id
            language
            slug {
              current
            }
          }
          ... on SanityPage {
            id
            language
            slug {
              current
            }
          }
          ... on SanityExternalRedirectTarget {
            url
          }
        }
      }
      _rawPageContent(resolveReferences: { maxDepth: 10 })
    }
    sanitySiteSettings(language: { eq: $language }) {
      id
      header {
        registerCta
        loginCta
        navigation {
          externalUrl
          title
          page {
            ... on SanityPage {
              language
              title
              slug {
                current
              }
            }
            ... on SanityStartPage {
              language
              title
              slug {
                current
              }
            }
          }
        }
      }
      footer {
        metaNavigation {
          externalUrl
          title
          page {
            ... on SanityPage {
              title
              language
              slug {
                current
              }
            }
            ... on SanityStartPage {
              title
              language
              slug {
                current
              }
            }
          }
        }
        _rawContent(resolveReferences: { maxDepth: 10 })
      }
      seo {
        description
        title
      }
    }
  }
`;

export type PageData = {
  id: string;
  language: string;
  title: string;
  pageContent?: any;
  pageRedirect?: any;
  _rawPageContent?: any;
};

type QueryData = {
  sanityStartPage?: PageData;
  sanityPage?: PageData;
  sanitySiteSettings?: {
    id: string;
    header: HeaderData;
    footer: FooterData;
    seo: SEOData;
  };
};

interface PageTemplateProps {
  data: QueryData;
  pageContext: {
    language: string;
    id: string;
    availableLanguages?: string[];
  };
}

const PageTemplate: React.FC<PageTemplateProps> = ({
  data: { sanityPage, sanityStartPage, sanitySiteSettings },
  pageContext: { language, availableLanguages },
}) => {
  const pageData = sanityPage || sanityStartPage;
  const [storedLanguage, setStoredLanguage] = useState(null);

  const getSetLanguage = () => {
    const urlParams = new URLSearchParams(window.location.search);

    let setLanguage = urlParams.get("setLanguage");

    if (setLanguage) {
      //When we have language as a parameter we have to change cookies and set the right language
      document.cookie = `preferredLang=${setLanguage}; path=/; expires=${getExpirationYears(
        COOKIE_EXPIRATION.LANDING_PAGE_PREFERRED_LANGUAGE_YEARS
      )};`; //this cookie will expire in 10 years
      document.cookie = `classpad_language=${setLanguage}; path=/;`; //this cookie will expire in 24 hours
    }
    return setLanguage || null;
  };

  const getClassPadLanguage = () => {
    let classpad_language = document.cookie
      .split("; ")
      .find((row) => row.startsWith("classpad_language="))
      ?.split("=")[1];

    //In order to sync the language with the CPA application we will check the classpad_language cookie, if exist we will use that language.
    if (classpad_language) {
      document.cookie = `preferredLang=${classpad_language}; path=/; expires=${getExpirationYears(
        COOKIE_EXPIRATION.LANDING_PAGE_PREFERRED_LANGUAGE_YEARS
      )};`; //this cookie will expire in 10 years
    }

    return classpad_language || null;
  };

  const getPrefferedLanguage = () => {
    let preferredLang = document.cookie
      .split("; ")
      .find((row) => row.startsWith("preferredLang="))
      ?.split("=")[1];

    return preferredLang || null;
  };

  const getBrowserLanguage = () => {
    let browserLang =
      (navigator?.language || "").split("-")[0] || defaultLanguageCode;
    return availableLanguages?.includes(browserLang)
      ? browserLang
      : defaultLanguageCode;
  };

  const injectMatomo = () => {
    if (typeof window === "undefined") return;

    if (!document.getElementById("matomo-script")) {
      // Initialize _paq
      window._paq = window._paq || [];
      window._paq.push(["trackPageView"]);
      window._paq.push(["enableLinkTracking"]);
      window._paq.push([
        "setTrackerUrl",
        "https://casioeducation.matomo.cloud/matomo.php",
      ]);
      window._paq.push(["setSiteId", "5"]);

      // Create the script element
      const script = document.createElement("script");
      script.id = "matomo-script";
      script.async = true;
      script.src =
        "https://cdn.matomo.cloud/casioeducation.matomo.cloud/matomo.js";

      // Insert script before the first script tag in the document
      const firstScript = document.getElementsByTagName("script")[0];
      firstScript?.parentNode?.insertBefore(script, firstScript);
    }
  };

  useEffect(() => {
    injectMatomo();
    (async () => {
      if (typeof window != "undefined") {
        await sleep(1000);
        const setLanguage = getSetLanguage();
        const classpadLanguage = getClassPadLanguage();
        const preferredLanguage = getPrefferedLanguage();
        const browserLanguage = getBrowserLanguage();

        const _storedLang =
          setLanguage ||
          classpadLanguage ||
          preferredLanguage ||
          browserLanguage;

        console.log({
          setLanguage,
          classpadLanguage,
          preferredLanguage,
          browserLanguage,
          _storedLang,
        });
        console.log(`setLanguage:${setLanguage}`);
        console.log(`classpadLanguage:${classpadLanguage}`);
        console.log(`preferredLanguage:${preferredLanguage}`);
        console.log(`browserLanguage:${browserLanguage}`);
        console.log(`_storedLang:${_storedLang}`);
        console.log(`storedLanguage:${storedLanguage}`);

        setStoredLanguage(_storedLang);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!storedLanguage) return;

      // Prevent infinite redirect loop
      const currentPath = window.location.pathname;
      const expectedBasePath = getBasePathForLanguage(storedLanguage);

      //const alreadyRedirected = sessionStorage.getItem("languageRedirectDone");

      const shouldRedirect =
        // !alreadyRedirected &&
        availableLanguages?.includes(storedLanguage) &&
        language !== storedLanguage &&
        !currentPath.startsWith(expectedBasePath);

      //Old logic to redirect
      const redirectPath = getRedirectPath(pageData?.pageRedirect);
      console.log({
        shouldRedirect,
        redirectPath,
        expectedBasePath,
        storedLanguage,
      });

      console.log(`shouldRedirect:${shouldRedirect}`);
      console.log(`redirectPath:${redirectPath}`);
      console.log(`expectedBasePath:${expectedBasePath}`);
      console.log(`storedLanguage:${storedLanguage}`);

      if (redirectPath) {
        window.location.replace(redirectPath);
      } else if (shouldRedirect) {
        const newPath = getBasePathForLanguage(storedLanguage);
        window.location.replace(newPath);
      }
    })();
  }, [storedLanguage]);

  return (
    <Layout>
      <SEO
        pageTitle={pageData?.title}
        data={sanitySiteSettings?.seo}
        language={language}
      />
      <Header
        language={language}
        data={sanitySiteSettings?.header}
        availableLanguages={availableLanguages}
      />
      <main className={styles.main}>
        <BlockContent
          blocks={pageData?._rawPageContent || pageData?.pageContent}
          language={language}
        />
      </main>
      <Footer language={language} data={sanitySiteSettings?.footer} />
    </Layout>
  );
};

const getRedirectPath = (pageRedirect: any) => {
  const redirectItem = pageRedirect?.redirectTarget[0];

  if (!redirectItem) {
    return false;
  } else if (redirectItem?.url) {
    return redirectItem.url;
  } else if (redirectItem?.slug?.current) {
    const path = redirectItem.slug.current;
    return getBasePathForLanguage(redirectItem?.language) + stripSlashes(path);
  } else {
    return false;
  }
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default PageTemplate;
